import { useCallback, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useIncidents, INCIDENTS_QUERY_KEY } from '../../incidents/hooks/useIncidents'
import {
  useTaskNotificationStream,
  type TaskNotification,
} from '../../incidents/hooks/useTaskNotificationStream'
import { ACTIVE_ALERT_STATUSES, computeKpis, isToday } from '../utils/liveOpsAggregate'
import type { Incident } from '../../incidents/types/incident.types'
import type { StatCard } from '../types/liveOps.types'

const LIVE_OPS_REFETCH_INTERVAL = 20000

const SECURITY_TASK_STATUSES = new Set<Incident['status']>(['NEW', 'ACKNOWLEDGE', 'IN_PROGRESS'])

interface LiveOpsData {
  kpis: StatCard[]
  securityTasks: Incident[]
  alerts: Incident[]
  incidentsToday: Incident[]
  draftTasks: Incident[]
}

interface UseLiveOpsResult {
  data: LiveOpsData | undefined
  isLoading: boolean
  isError: boolean
  refetch: () => void
}

export function useLiveOps(): UseLiveOpsResult {
  const queryClient = useQueryClient()
  const incidentsQuery = useIncidents({ refetchInterval: LIVE_OPS_REFETCH_INTERVAL })

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: [INCIDENTS_QUERY_KEY] })
  }, [queryClient])

  useTaskNotificationStream({
    onNew: useCallback((_n: TaskNotification) => invalidate(), [invalidate]),
    onDraft: useCallback((_n: TaskNotification) => invalidate(), [invalidate]),
  })

  const incidents = incidentsQuery.data

  const data = useMemo<LiveOpsData | undefined>(() => {
    if (!incidents) return undefined
    const now = new Date()
    return {
      kpis: computeKpis(incidents, now),
      securityTasks: incidents.filter(
        (i) => SECURITY_TASK_STATUSES.has(i.status) && (i.intent != null || i.location != null),
      ),
      alerts: incidents.filter(
        (i) => i.priority === 'CRITICAL' && ACTIVE_ALERT_STATUSES.has(i.status),
      ),
      incidentsToday: incidents.filter((i) => isToday(i.created_at, now)),
      draftTasks: incidents.filter((i) => i.status === 'DRAFT' && i.source === 'ai'),
    }
  }, [incidents])

  return {
    data,
    isLoading: incidentsQuery.isLoading,
    isError: incidentsQuery.isError,
    refetch: () => {
      void incidentsQuery.refetch()
    },
  }
}
